import { useEffect, useState } from "react";
import { TransactionsList } from "../utils/types";
import {
  Button,
  ButtonGroup,
  Center,
  Checkbox,
  CheckboxGroup,
  HStack,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { getHeadersForRequest } from "../utils/functions";
import axios from "axios";
import { Loading } from "../components/Loading";
import { FaEye, FaPencilAlt, FaTrash } from "react-icons/fa";
import Nav from "../components/navbar";
import { TransactionCard } from "../components/TransactionCard";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

const RecentOrdersScreen = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<TransactionsList[]>([]);
  const [invoiceNumber, setInvoiceNumber] = useState<number>(0);

  const navigate = useNavigate();
  const [groupValues, setGroupValues] = useState<string[]>([]);
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState<string>("");
  const [transactionId, setTransactionId] = useState<number>(0);
  const [traReceiptNumber, setTraReceiptNumber] = useState<string>("");
  const [traReceiptTime, setTraReceiptTime] = useState<string>("");
  const toast = useToast();

  useEffect(() => {
    setIsLoading(true);
    getAllTransactions();
  }, []);

  const navigateToOrderDetails = (orderId: string) => {
    navigate(`/OrderDetails/${orderId}`);
  };

  const handleTRApost = async () => {
    if (invoiceNumber === 0) {
      toast({
        description: "Failed to save Upload Details ",
        status: "error",
      });
      return;
    } else {
      setModalIsLoading(true);

      try {
        const headers = await getHeadersForRequest();

        const response = await axios.post(
          `${process.env.REACT_APP_TEST_API_URL}/transactions/save/traPost`,
          { transactionId: invoiceNumber },
          { headers }
        );
        // console.log(response.data);
        if (response.data.success === 1) {
          toast({
            title: "Successfully uploaded details to TRA",
            status: "success",
          });
          // handleClearList();
          setModalIsLoading(false);
          getAllTransactions();
          setShowDetailsModal(false);
        } else {
          toast({
            title: response.data.message,
            status: "error",
          });
          setModalIsLoading(false);
          getAllTransactions();
          setShowDetailsModal(false);
        }
      } catch (error: any) {
        console.error(error);
        toast({
          title: error.message,
          status: "error",
        });
        setModalIsLoading(false);
      }
    }
  };

  const getAllTransactions = async () => {
    try {
      const today = new Date();
      today.setDate(today.getDate() - 100);
      const startDate = today;
      const endDate = new Date();

      const oldHeaders = await getHeadersForRequest();
      const newHeaders = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
      const headers = { ...oldHeaders, ...newHeaders };

      const response = await axios.get<TransactionsList[]>(
        `${process.env.REACT_APP_TEST_API_URL}/transactions/getAll`,
        {
          headers,
        }
      );

      const filteredOrdersDeliveryPending = await response.data.filter(
        (transaction) =>
          transaction.deliveryStatus === false && transaction.isVoid === false
      );
      const filteredOrdersPaymentPending = await response.data.filter(
        (transaction) => transaction.isVoid === false
      );

      setTransactions(
        filteredOrdersPaymentPending.sort(
          (a, b) =>
            new Date(b.invoiceDate).getTime() -
            new Date(a.invoiceDate).getTime()
        )
      );
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const groupedTransactions: {
    date: string;
    transactions: TransactionsList[];
  }[] = [];

  // Sort the groupedTransactions array in descending order of transaction date
  groupedTransactions.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });

  for (let transaction of transactions) {
    const date = new Date(transaction.invoiceDate).toLocaleDateString();
    const existingGroup = groupedTransactions.find(
      (group) => group.date === date
    );
    if (existingGroup) {
      existingGroup.transactions.push(transaction);
    } else {
      groupedTransactions.push({ date, transactions: [transaction] });
    }
  }

  const handleTransactionClick = (transaction: TransactionsList) => {
    setShowDetailsModal(true);

    let delivered = transaction.deliveryStatus ? "delivered" : "";
    let paid = transaction.paymentStatus ? "paid" : "";
    let physical = transaction.physicalDeliveryNote ? "physical" : "";
    let tra = transaction.traReceiptNumber && transaction.traTime ? "tra" : "";
    let newGroupValues = [delivered, paid, physical, tra];

    setGroupValues(newGroupValues);
    setDeliveryNoteNumber(transaction.deliveryNoteNumber);
    setTransactionId(transaction.id);
    setInvoiceNumber(parseInt(transaction.transactionNumber));
    setTraReceiptNumber(transaction.traReceiptNumber);
    setTraReceiptTime(transaction.traTime);
  };

  const updateTransactionDetails = async () => {
    setModalIsLoading(true);
    try {
      const data = {
        paymentStatus: groupValues.includes("paid"),
        paymentDate: groupValues.includes("paid") ? new Date() : null,
        transactionId: transactionId,
        deliveryStatus: groupValues.includes("delivered"),
        deliveryDate: groupValues.includes("delivered") ? new Date() : null,
        invoiceDate: new Date(),
        physicalDeliveryNote: groupValues.includes("physical"),
        deliveryNoteNumber: groupValues.includes("physical")
          ? deliveryNoteNumber
          : "",
        traReceiptNumber: traReceiptNumber,
        traReceiptTime: traReceiptTime,
        account: 0,
      };

      if (data.deliveryNoteNumber === "" && data.physicalDeliveryNote) {
        toast({
          title: "Invalid Delivery Note Number",
          status: "warning",
          duration: 9000,
          isClosable: true,
        });
        setModalIsLoading(false);
        return;
      }

      const headers = await getHeadersForRequest();
      await axios
        .post(
          `${process.env.REACT_APP_TEST_API_URL}/transactions/update`,
          {
            data,
          },
          { headers }
        )
        .then((response) => {
          console.log(response.data);
          toast({
            title: "Transaction details updated successfully",
            status: "success",
            duration: 9000,
            isClosable: true,
          });
          setShowDetailsModal(false);
          getAllTransactions();
          setModalIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setModalIsLoading(false);
          toast({
            title: "Error updating transaction",
            status: "warning",
            duration: 9000,
            isClosable: true,
          });
        });
    } catch (error) {}
  };

  const voidTransaction = async () => {
    setIsLoading(true);
    try {
      const headers = await getHeadersForRequest();
      await axios
        .post(
          `${process.env.REACT_APP_TEST_API_URL}/transactions/delete`,
          {
            transactionId: transactionId,
          },
          { headers }
        )
        .then((response) => {
          console.log(response.data);
          toast({
            title: "Transaction VOID successful",
            status: "success",
            duration: 9000,
            isClosable: true,
          });
          setShowDetailsModal(false);
          setShowConfirmationModal(false);
          getAllTransactions();
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          toast({
            title: "Error, please try again later",
            status: "warning",
            duration: 9000,
            isClosable: true,
          });
          setIsLoading(false);
          setShowConfirmationModal(false);
        });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  return (
    <Stack>
      <Stack position="sticky" top={0} zIndex={1} width="full">
        <Nav pageName={"Sale History"}/>
      </Stack>
      <Stack flex={1}>
        <Stack pt={2} pb={10} px={3}>
          {isLoading ? (
            <Center h={600}>
              <Loading />
            </Center>
          ) : (
            <Stack>
              <Text>Record count: {groupedTransactions.length}</Text>
              {groupedTransactions.map((groupedTransaction, index) => (
                <Stack key={index}>
                  <Heading size="md" my={2}></Heading>
                  {groupedTransaction.transactions.map((transaction, index) => (
                    <HStack>
                      <Stack
                        flex={1}
                        key={index}
                        onContextMenu={(e) => {
                          e.preventDefault();

                          if (transaction.isVoid) {
                            toast({
                              title: "Cannot edit a VOID receipt",
                              status: "warning",
                              duration: 9000,
                              isClosable: true,
                            });
                          } else {
                            handleTransactionClick(transaction);
                          }
                        }}
                        onClick={() =>
                          navigateToOrderDetails(transaction.transactionNumber)
                        }
                      >
                        <TransactionCard transaction={transaction} />
                      </Stack>

                      <Stack>
                        <IconButton
                          size="lg"
                          fontSize="lg"
                          // variant="ghost"
                          // color="current"
                          marginLeft="2"
                          onClick={() => {
                            if (transaction.isVoid) {
                              toast({
                                description: "Cannot edit a VOID receipt",
                                status: "warning",
                                duration: 9000,
                                isClosable: true,
                              });
                            } else {
                              handleTransactionClick(transaction);
                            }
                          }}
                          icon={<FaPencilAlt />}
                          aria-label={`Edit Transaction Details`}
                        />

                        <IconButton
                          size="lg"
                          fontSize="lg"
                          // variant="ghost"
                          // color="current"
                          marginLeft="2"
                          onClick={() => {
                            navigateToOrderDetails(
                              transaction.transactionNumber
                            );
                          }}
                          icon={<FaEye />}
                          aria-label={`View Transaction Details`}
                        />
                      </Stack>
                    </HStack>
                  ))}
                </Stack>
              ))}
              {groupedTransactions.length < 1 && (
                <Stack alignItems={"center"} justifyContent={"center"} h={400}>
                  <Text fontSize={20} textAlign={"center"}>
                    There are no Transactions to show
                  </Text>
                  <Text fontSize={20} textAlign={"center"}>
                    Go to the Sales screen to record your first transaction!
                  </Text>
                </Stack>
              )}
            </Stack>
          )}

          {/* TRANSACTION DETAILS MODAL */}
          <Modal
            isCentered
            closeOnOverlayClick={false}
            isOpen={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
          >
            <ModalContent maxWidth="350">
              <ModalCloseButton />
              <ModalHeader>Transaction Details</ModalHeader>
              <ModalBody>
                <HStack justifyContent={"space-between"}>
                  <Text>Order details:</Text>

                  <IconButton
                    size="md"
                    fontSize="lg"
                    variant="ghost"
                    color="current"
                    marginLeft="2"
                    onClick={() => setShowConfirmationModal(true)}
                    icon={<FaTrash color="red" />}
                    aria-label={`Go to home`}
                  />
                </HStack>

                <Stack>
                  <CheckboxGroup
                    onChange={(values: string[]) => setGroupValues(values)}
                    value={groupValues}
                  >
                    <Checkbox
                      disabled={groupValues.includes("paid")}
                      value="paid"
                      my={2}
                    >
                      Paid
                    </Checkbox>
                    <Checkbox value="delivered" my={2}>
                      Delivered
                    </Checkbox>
                    <Checkbox value="physical" my={2}>
                      Physical Delivery Note
                    </Checkbox>
                    {Cookies.get("businessName") === "Keinth's Food" && (
                      <Checkbox value="tra" my={2}>
                        TRA Receipt
                      </Checkbox>
                    )}
                  </CheckboxGroup>
                </Stack>

                {/* {!traReceiptNumber &&
                  Cookies.get("businessName") !== "Keinth's Food" && (
                    <Center py={2}>
                      <Button
                        isLoading={modalIsLoading}
                        onClick={() => handleTRApost()}
                      >
                        FISCALISE
                      </Button>
                    </Center>
                  )} */}

                <Stack>
                  {groupValues.includes("physical") && (
                    <Input
                      borderWidth={3}
                      fontSize="md"
                      pl={5}
                      mb={2}
                      inputMode="numeric"
                      backgroundColor="transparent"
                      variant={"underlined"}
                      value={deliveryNoteNumber}
                      onChange={(e) => setDeliveryNoteNumber(e.target.value)}
                      placeholder={"Enter delivery note number"}
                    />
                  )}
                </Stack>

                <Stack>
                  {groupValues.includes("tra") && (
                    <>
                      <Input
                        borderWidth={3}
                        fontSize="md"
                        pl={5}
                        inputMode="numeric"
                        value={traReceiptNumber}
                        backgroundColor="transparent"
                        variant={"underlined"}
                        onChange={(e) => setTraReceiptNumber(e.target.value)}
                        placeholder={"Enter TRA Receipt Number"}
                      />

                      <Input
                        borderWidth={3}
                        fontSize="md"
                        pl={5}
                        value={traReceiptTime}
                        backgroundColor="transparent"
                        inputMode="numeric"
                        variant={"underlined"}
                        onChange={(e) => setTraReceiptTime(e.target.value)}
                        placeholder={"Enter Receipt Time"}
                      />
                    </>
                  )}
                </Stack>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="ghost"
                    colorScheme="blueGray"
                    onClick={() => {
                      setShowDetailsModal(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    isLoading={modalIsLoading}
                    onClick={() => {
                      updateTransactionDetails();
                    }}
                  >
                    Update
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* CONFIRMATION MODAL */}
          <Modal
            isCentered
            closeOnOverlayClick={false}
            isOpen={showConfirmationModal}
            onClose={() => setShowConfirmationModal(false)}
          >
            <ModalContent maxWidth="350">
              <ModalCloseButton />
              <ModalHeader>Alert</ModalHeader>
              <ModalBody>
                <Stack>
                  <Text textAlign={"center"}>
                    Are you sure you want to VOID this receipt?
                  </Text>
                  <Text textAlign={"center"}>THIS ACTION IS IRREVERSIBLE!</Text>
                </Stack>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="ghost"
                    colorScheme="blueGray"
                    onClick={() => {
                      setShowConfirmationModal(false);
                    }}
                  >
                    No
                  </Button>
                  <Button
                    isLoading={isLoading}
                    bg={"red"}
                    onClick={() => {
                      voidTransaction();
                    }}
                  >
                    Yes
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default RecentOrdersScreen;
